import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  CircularProgress,
  TextField,
  MenuItem,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  Assessment,
  ArrowBack,
  CalendarMonth,
  FlightTakeoff,
  AccessTime,
  Place,
  Air,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

interface YearlyReport {
  year: number;
  total_flights: number;
  total_flight_time_minutes: number;
  total_flight_time_hours: number;
  solo_flights: number;
  tandem_flights: number;
  flights_by_month: { [key: string]: number };
  unique_sites: number;
  wings_used: string[];
}

const YearlyReportPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [report, setReport] = useState<YearlyReport | null>(null);
  const [loading, setLoading] = useState(false);

  const years = Array.from({ length: 10 }, (_, i) => currentYear - i);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/logbook/yearly-report/${year}`);
      setReport(response.data);
    } catch (err) {
      console.error('Failed to fetch report:', err);
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getMonthName = (monthNum: number) => {
    return new Date(2000, monthNum - 1).toLocaleDateString(i18n.language, { month: 'short' });
  };

  const maxFlightsInMonth = report
    ? Math.max(...Object.values(report.flights_by_month))
    : 0;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper sx={{ p: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button startIcon={<ArrowBack />} onClick={() => navigate('/app/logbook')}>
              {t('logbook.back')}
            </Button>
            <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assessment color="primary" />
              {t('logbook.yearlyReport')}
            </Typography>
          </Box>
          <TextField
            select
            size="small"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            sx={{ minWidth: 120 }}
          >
            {years.map((y) => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </TextField>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : report ? (
          <>
            {/* Summary Cards */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
              <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <FlightTakeoff sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h3" color="primary" sx={{ fontWeight: 700 }}>
                    {report.total_flights}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('logbook.stats.totalFlights')}
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <AccessTime sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                  <Typography variant="h3" color="success.main" sx={{ fontWeight: 700 }}>
                    {report.total_flight_time_hours}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('logbook.stats.totalHours')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({formatDuration(report.total_flight_time_minutes)})
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Place sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
                  <Typography variant="h3" color="secondary.main" sx={{ fontWeight: 700 }}>
                    {report.unique_sites}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('logbook.stats.uniqueSites')}
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Flight Types */}
            <Paper sx={{ p: 3, mb: 4 }} variant="outlined">
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('logbook.flightTypeSection')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 4 }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Typography variant="h4" color="primary">{report.solo_flights}</Typography>
                    <Typography variant="body1">{t('logbook.solo')}</Typography>
                  </Box>
                  <Box
                    sx={{
                      height: 8,
                      bgcolor: 'primary.main',
                      borderRadius: 1,
                      width: `${report.total_flights > 0 ? (report.solo_flights / report.total_flights) * 100 : 0}%`,
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Typography variant="h4" color="secondary">{report.tandem_flights}</Typography>
                    <Typography variant="body1">{t('logbook.tandem')}</Typography>
                  </Box>
                  <Box
                    sx={{
                      height: 8,
                      bgcolor: 'secondary.main',
                      borderRadius: 1,
                      width: `${report.total_flights > 0 ? (report.tandem_flights / report.total_flights) * 100 : 0}%`,
                    }}
                  />
                </Box>
              </Box>
            </Paper>

            {/* Monthly Distribution */}
            <Paper sx={{ p: 3, mb: 4 }} variant="outlined">
              <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarMonth color="primary" />
                {t('logbook.stats.monthlyDistribution')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', height: 150 }}>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => {
                  const count = report.flights_by_month[month] || 0;
                  const heightPercent = maxFlightsInMonth > 0 ? (count / maxFlightsInMonth) * 100 : 0;
                  return (
                    <Box key={month} sx={{ flex: 1, textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {count || ''}
                      </Typography>
                      <Box
                        sx={{
                          height: `${Math.max(heightPercent, count > 0 ? 10 : 0)}%`,
                          minHeight: count > 0 ? 10 : 0,
                          bgcolor: count > 0 ? 'primary.main' : 'grey.200',
                          borderRadius: '4px 4px 0 0',
                          transition: 'height 0.3s',
                          mb: 1,
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {getMonthName(month)}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Paper>

            {/* Wings Used */}
            {report.wings_used.length > 0 && (
              <Paper sx={{ p: 3 }} variant="outlined">
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Air color="primary" />
                  {t('logbook.stats.wingsUsed')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {report.wings_used.map((wing) => (
                    <Chip key={wing} label={wing} variant="outlined" />
                  ))}
                </Box>
              </Paper>
            )}

            {/* Empty State */}
            {report.total_flights === 0 && (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <FlightTakeoff sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  {t('logbook.noFlightsForYear', { year })}
                </Typography>
              </Box>
            )}
          </>
        ) : null}
      </Paper>
    </Container>
  );
};

export default YearlyReportPage;
